import format from "date-fns/format";
import getDay from "date-fns/getDay";
import isSameDay from "date-fns/isSameDay";
import { enUS } from "date-fns/locale";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import Cookies from "js-cookie";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PayrollTable from "../../Components/PayrollTable/PayrollTable";
import "../../Page/Payroll/Payroll.css";
import {
  fetchDatewisePayrollData,
  fetchPayrollDataByMemberId,
  fetchPayrollDropdown,
} from "../../Redux/Slice/Payroll";
import InhouseMonthlyPayroll from "./InhouseMonthlyPayroll/InhouseMonthlyPayroll";
import Loader from "../../Components/Loader/Loader";
import { fetchAllLeavesHistory } from "../../Redux/Slice/LeaveForm";

import axios from "axios";

const locales = { "en-US": enUS };
const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Helper functions with error handling
function formatUTCToHHMM(isoString) {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;

    let hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
}

function getDuration(startTime, endTime) {
  if (!startTime || !endTime) return "";
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";
    const diffMs = end - start;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error("Error calculating duration:", error);
    return "";
  }
}

const formatMillisecondsToHHMM = (ms) => {
  if (ms < 0 || isNaN(ms)) ms = 0;
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${minutes}m`;
};

const formatTimeToShow = (timeStr) => {
  if (!timeStr) return "10:00 AM";
  const [h, m] = timeStr.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return timeStr;
  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;
  const displayMin = m < 10 ? `0${m}` : m;
  return `${displayHour}:${displayMin} ${ampm}`;
};

const InhousePayroll = () => {
  const dispatch = useDispatch();

  const { payrollDropdownData, MemberData, DatewiseData } = useSelector(
    (state) => state.Payroll || {
      payrollDropdownData: null,
      MemberData: null,
      DatewiseData: null
    }
  );

  const { leaveData } = useSelector(
    (state) => state.LeaveForm || { leaveData: [] }
  );

  const loginData = Cookies.get("loginData");
  const LoginUserData = loginData ? JSON.parse(loginData) : null;

  const [activeDate, setActiveDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showData, setShowData] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [matchedValue, setMatchedValue] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [scheduleSettings, setScheduleSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchHolidaysAndSchedule = async () => {
      try {
        const [hRes, sRes] = await Promise.all([
          axios.get(`${BaseUrl}/Chat/holidays`),
          axios.get(`${BaseUrl}/Chat/schedule-settings`)
        ]);
        setHolidays(hRes.data?.data || []);
        if (sRes.data?.status === "success") {
          setScheduleSettings(sRes.data);
        }
      } catch (err) {
        console.error("Error fetching holidays or schedule in InhousePayroll:", err);
      }
    };
    fetchHolidaysAndSchedule();
  }, []);

  const holidayMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(holidays)) {
      holidays.forEach((h) => {
        if (h.dateStr) map.set(h.dateStr, h.name || "Company Holiday");
      });
    }
    return map;
  }, [holidays]);

  const lastSchedulerInMinutes = useMemo(() => {
    const times = scheduleSettings?.times;
    if (Array.isArray(times) && times.length > 0) {
      const sorted = [...times].sort();
      const last = sorted[sorted.length - 1];
      if (last && last.includes(":")) {
        const [h, m] = last.split(":").map(Number);
        return (isNaN(h) ? 10 : h) * 60 + (isNaN(m) ? 0 : m);
      }
    }
    return 10 * 60; // default 10:00 AM (600 mins)
  }, [scheduleSettings]);

  useEffect(() => {
    dispatch(fetchPayrollDropdown());
  }, [dispatch]);

  const memberOptions = useMemo(() => {
    if (!payrollDropdownData?.data || !Array.isArray(payrollDropdownData.data)) {
      return [];
    }
    return payrollDropdownData.data
      .filter(item => item && item.MemberName && item.MemberId)
      .map((item) => ({
        label: item.MemberName,
        value: item.MemberId,
        CheckInTime: item.CheckInTime || "10:00",
        CheckOutTime: item.CheckOutTime || "19:00",
      }));
  }, [payrollDropdownData]);

  const selectedMemberOption = useMemo(() => {
    return memberOptions.find(opt => String(opt.value) === String(matchedValue));
  }, [memberOptions, matchedValue]);



  // Auto-select member based on login or query param
  useEffect(() => {
    if (memberOptions.length === 0) return;

    // Check if memberId is passed in the URL or state first
    const searchParams = new URLSearchParams(location.search);
    const passedMemberId = location.state?.memberId || searchParams.get("memberId");
    if (passedMemberId) {
      const matched = memberOptions.find(
        (opt) => String(opt.value) === String(passedMemberId)
      );
      if (matched) {
        setMatchedValue(matched.value);
        setIsInitialized(true);
        return;
      }
    }

    if (!LoginUserData?.data?.Name) {
      setIsInitialized(true);
      return;
    }

    try {
      const fullName = LoginUserData.data.Name.toLowerCase();
      const [firstName, lastName] = fullName.split(" ") || ["", ""];

      const firstMatches = memberOptions.filter((member) =>
        member.label.toLowerCase().includes(firstName)
      );

      let matchedId = null;
      if (firstMatches.length === 1) {
        matchedId = firstMatches[0].value;
      } else if (firstMatches.length > 1 && lastName) {
        const fullMatches = firstMatches.filter((member) =>
          member.label.toLowerCase().includes(lastName)
        );
        if (fullMatches.length === 1) {
          matchedId = fullMatches[0].value;
        }
      }

      setMatchedValue(matchedId);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error matching member:", error);
      setIsInitialized(true);
    }
  }, [LoginUserData, memberOptions, location]);

  // Fetch member data after auto-selection
  useEffect(() => {
    if (matchedValue && isInitialized) {
      if (!MemberData) {
        dispatch(fetchPayrollDataByMemberId(matchedValue))
          .catch(error => {
            console.error("Error fetching member data:", error);
            setCalendarError("Failed to fetch member data");
          });
      }
      dispatch(fetchAllLeavesHistory())
        .catch(error => {
          console.error("Error fetching leaves:", error);
        });
    }
  }, [matchedValue, MemberData, dispatch, isInitialized]);

  const inOutTableRows = useMemo(() => {
    if (!Array.isArray(DatewiseData?.data) || DatewiseData.data.length === 0) return [];

    const punches = [];
    DatewiseData.data.forEach((item) => {
      if (!item) return;

      if (item.InDate) {
        try {
          const d = new Date(item.InDate);
          if (!isNaN(d.getTime())) {
            punches.push({
              type: "in",
              time: d,
              original: item.InDate,
            });
          }
        } catch (e) {
          console.error("Invalid InDate:", e);
        }
      }

      if (item.OutDate) {
        try {
          const d = new Date(item.OutDate);
          if (!isNaN(d.getTime())) {
            punches.push({
              type: "out",
              time: d,
              original: item.OutDate,
            });
          }
        } catch (e) {
          console.error("Invalid OutDate:", e);
        }
      }
    });

    if (punches.length === 0) return [];

    // Sort punches chronologically
    punches.sort((a, b) => a.time.getTime() - b.time.getTime());

    const pairs = [];
    let currentIn = null;

    punches.forEach((punch) => {
      if (punch.type === "in") {
        if (currentIn) {
          pairs.push({
            InDate: formatUTCToHHMM(currentIn.original) || "-",
            OutDate: "-",
          });
        }
        currentIn = punch;
      } else if (punch.type === "out") {
        if (currentIn) {
          pairs.push({
            InDate: formatUTCToHHMM(currentIn.original) || "-",
            OutDate: formatUTCToHHMM(punch.original) || "-",
          });
          currentIn = null;
        } else {
          pairs.push({
            InDate: "-",
            OutDate: formatUTCToHHMM(punch.original) || "-",
          });
        }
      }
    });

    if (currentIn) {
      pairs.push({
        InDate: formatUTCToHHMM(currentIn.original) || "-",
        OutDate: "-",
      });
    }

    return pairs.map((pair, index) => ({
      id: index + 1,
      InDate: pair.InDate,
      OutDate: pair.OutDate,
    }));
  }, [DatewiseData?.data]);


  const inOutTableColumns = [
    { field: "id", headerName: "ID", width: 60, align: "center" },
    { field: "InDate", headerName: "In Time", width: "calc(50% - 30px)", align: "center" },
    { field: "OutDate", headerName: "Out Time", width: "calc(50% - 30px)", align: "center" },
  ];

  const handleSelectSlot = useCallback(
    (slotInfo) => {
      try {
        if (!slotInfo || !slotInfo.start) {
          console.error("Invalid slot info:", slotInfo);
          return;
        }

        const clickedDate = slotInfo.start;
        if (!matchedValue) {
          alert("Please select a member first.");
          return;
        }

        setIsLoading(true);
        setShowData(false);
        setCalendarError(null);
        setActiveDate(clickedDate);

        dispatch(
          fetchDatewisePayrollData({
            memberId: matchedValue,
            selectedDate: clickedDate.toISOString(),
          })
        )
          .unwrap()
          .then(() => {
            console.log("API call successful.");
            setIsLoading(false);
            setShowData(true);
          })
          .catch((err) => {
            console.error("API call failed:", err);
            setIsLoading(false);
            setShowData(true);
            setCalendarError("Failed to load data for selected date");
          });
      } catch (error) {
        console.error("Error in handleSelectSlot:", error);
        setIsLoading(false);
        setCalendarError("An error occurred");
      }
    },
    [matchedValue, dispatch]
  );

  const handleSelectEvent = useCallback(
    (event) => {
      try {
        if (!event || !event.start) {
          console.error("Invalid event:", event);
          return;
        }

        const clickedDate = event.start;
        if (!matchedValue) {
          alert("Please select a member first.");
          return;
        }

        setIsLoading(true);
        setShowData(false);
        setCalendarError(null);
        setActiveDate(clickedDate);

        dispatch(
          fetchDatewisePayrollData({
            memberId: matchedValue,
            selectedDate: clickedDate.toISOString(),
          })
        )
          .unwrap()
          .then(() => {
            console.log("API call successful for event.");
            setIsLoading(false);
            setShowData(true);
          })
          .catch((err) => {
            console.error("API call failed for event:", err);
            setIsLoading(false);
            setShowData(true);
            setCalendarError("Failed to load data for selected date");
          });
      } catch (error) {
        console.error("Error in handleSelectEvent:", error);
        setIsLoading(false);
        setCalendarError("An error occurred");
      }
    },
    [matchedValue, dispatch]
  );

  const selectedMemberName = useMemo(() => {
    const selectedMemberOption = memberOptions.find(opt => String(opt.value) === String(matchedValue));
    return selectedMemberOption ? selectedMemberOption.label : (LoginUserData?.data?.Name || "");
  }, [memberOptions, matchedValue, LoginUserData]);

  const groupedByDate = useMemo(() => {
    if (!Array.isArray(MemberData?.data)) return {};
    const grouped = {};
    MemberData.data.forEach((item) => {
      if (!item) return;

      const inDateStr = item.InDate ? (typeof item.InDate === 'string' ? item.InDate : new Date(item.InDate).toISOString()) : null;
      const outDateStr = item.OutDate ? (typeof item.OutDate === 'string' ? item.OutDate : new Date(item.OutDate).toISOString()) : null;

      const dateKey = inDateStr
        ? inDateStr.slice(0, 10)
        : outDateStr
          ? outDateStr.slice(0, 10)
          : null;

      if (!dateKey) return;

      if (!grouped[dateKey]) {
        grouped[dateKey] = { firstIn: null, lastOut: null };
      }

      if (inDateStr) {
        try {
          const inTime = new Date(inDateStr.slice(0, 19));
          if (!isNaN(inTime.getTime())) {
            const currentFirstInTime = grouped[dateKey].firstIn ? new Date(grouped[dateKey].firstIn.slice(0, 19)) : null;
            if (!currentFirstInTime || inTime < currentFirstInTime) {
              grouped[dateKey].firstIn = inDateStr;
            }
          }
        } catch (e) {
          console.error("Invalid InDate:", e);
        }
      }

      if (outDateStr) {
        try {
          const outTime = new Date(outDateStr.slice(0, 19));
          if (!isNaN(outTime.getTime())) {
            const currentLastOutTime = grouped[dateKey].lastOut ? new Date(grouped[dateKey].lastOut.slice(0, 19)) : null;
            if (!currentLastOutTime || outTime > currentLastOutTime) {
              grouped[dateKey].lastOut = outDateStr;
            }
          }
        } catch (e) {
          console.error("Invalid OutDate:", e);
        }
      }
    });
    return grouped;
  }, [MemberData?.data]);

  const dayPropGetter = useCallback(
    (date) => {
      try {
        if (!date) return {};

        const formatYYYYMMDD = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const isLateIn = (isoString, checkInStr) => {
          if (!isoString) return false;
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return false;
          const hours = d.getUTCHours();
          const minutes = d.getUTCMinutes();
          const totalMinutes = hours * 60 + minutes;

          const [h, m] = (checkInStr || "10:00").split(":").map(Number);
          const target = (isNaN(h) ? 10 : h) * 60 + (isNaN(m) ? 0 : m);
          return totalMinutes > target;
        };

        const isEarlyOut = (isoString, checkOutStr) => {
          if (!isoString) return false;
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return false;
          const hours = d.getUTCHours();
          const minutes = d.getUTCMinutes();
          const totalMinutes = hours * 60 + minutes;

          const [h, m] = (checkOutStr || "19:00").split(":").map(Number);
          const target = (isNaN(h) ? 19 : h) * 60 + (isNaN(m) ? 0 : m);
          return totalMinutes < target;
        };

        const dayStr = formatYYYYMMDD(date);
        const dayOfWeek = date.getDay();
        const today = new Date();
        const todayStr = formatYYYYMMDD(today);
        const currentMinutes = today.getHours() * 60 + today.getMinutes();
        const checkInTime = selectedMemberOption?.CheckInTime || LoginUserData?.data?.CheckInTime || "10:00";
        const checkOutTime = selectedMemberOption?.CheckOutTime || LoginUserData?.data?.CheckOutTime || "19:00";

        const [hIn, mIn] = checkInTime.split(":").map(Number);
        const targetInMinutes = (isNaN(hIn) ? 10 : hIn) * 60 + (isNaN(mIn) ? 0 : mIn);
        const isAbsentToday = (dayStr === todayStr && currentMinutes >= lastSchedulerInMinutes && currentMinutes >= targetInMinutes);
        const isPendingToday = (dayStr === todayStr && !isAbsentToday);

        const [hOut, mOut] = checkOutTime.split(":").map(Number);
        const targetOutMinutes = (isNaN(hOut) ? 19 : hOut) * 60 + (isNaN(mOut) ? 0 : mOut);
        const isPastCheckOutTimeToday = (dayStr === todayStr && currentMinutes >= targetOutMinutes);
        const canBeEarlyOut = dayStr < todayStr || isPastCheckOutTimeToday;

        const isToday = isSameDay(date, today);
        const isActive = activeDate && isSameDay(date, activeDate);

        let status = 'none';
        const punch = groupedByDate[dayStr];

        if (punch && (punch.firstIn || punch.lastOut)) {
          const lateIn = isLateIn(punch.firstIn, checkInTime);
          const earlyOut = canBeEarlyOut && punch.lastOut ? isEarlyOut(punch.lastOut, checkOutTime) : false;
          const isRunning = (dayStr === todayStr) && !isPastCheckOutTimeToday;

          if (isRunning) {
            status = lateIn ? 'late' : 'running';
          } else if (lateIn && earlyOut) status = 'late-early';
          else if (lateIn) status = 'late';
          else if (earlyOut) status = 'early-out';
          else status = 'present';
        } else {
          const targetName = selectedMemberName ? selectedMemberName.trim().toLowerCase() : "";
          const approvedLeave = leaveData?.find(lv => {
            const isApproved = lv.Status === "Approved" || lv.status === "Approved";
            if (!isApproved) return false;
            const appName = (lv.applicantName || lv.applicant?.Name || "").trim().toLowerCase();
            return appName === targetName && lv.fromDate <= dayStr && lv.toDate >= dayStr;
          });

          if (approvedLeave) {
            status = 'leave';
          } else if (holidayMap.has(dayStr)) {
            status = 'holiday';
          } else if (dayOfWeek === 0) {
            status = 'weekend-off';
          } else if (dayStr < todayStr || isAbsentToday) {
            status = 'absent';
          } else if (isPendingToday) {
            status = 'pending';
          }
        }

        let backgroundColor = "white";
        if (isActive) {
          backgroundColor = "#e0f2f7";
        } else if (status === 'running') {
          backgroundColor = "#e0f2f1"; // Soft Teal/Green
        } else if (status === 'late') {
          backgroundColor = "#fff8e1"; // Soft Amber
        } else if (status === 'early-out') {
          backgroundColor = "#fff3e0"; // Soft Orange
        } else if (status === 'late-early') {
          backgroundColor = "#ffe0b2"; // Soft Deep Orange
        } else if (status === 'absent') {
          backgroundColor = "#ffebee"; // Soft Red
        } else if (status === 'pending') {
          backgroundColor = "#f8fafc"; // Soft Slate / Neutral Pending
        } else if (status === 'leave') {
          backgroundColor = "#f3e5f5"; // Soft Purple
        } else if (status === 'holiday') {
          backgroundColor = "#dcfce7"; // Soft Emerald / Green for Holiday
        } else if (status === 'weekend-off') {
          backgroundColor = "#e0f2fe"; // Soft Cyan/Blue for Weekend Off
        } else if (status === 'present') {
          backgroundColor = "#e8f5e9"; // Soft Green
        } else if (isToday) {
          backgroundColor = "#f0f0f0";
        }

        return {
          style: {
            backgroundColor,
            border: isActive ? "2px solid #007bff" : (status === 'pending' ? "1px dashed #cbd5e1" : "1px solid #e0e0e0"),
            borderRadius: "4px",
          },
        };
      } catch (error) {
        console.error("Error in dayPropGetter:", error);
        return {};
      }
    },
    [groupedByDate, activeDate, selectedMemberOption, LoginUserData, leaveData, selectedMemberName, holidayMap, lastSchedulerInMinutes]
  );

  const calculatedData = useMemo(() => {
    // Default return values
    const defaults = {
      inOutPairs: [],
      breakPairs: [],
      totalWorkingTime: "0h 0m",
      totalBreakTime: "0h 0m",
      adjustedTotalBreakTime: "0h 0m",
      companyBenefitTime: "0h 0m",
      companyBenefitFromWorkHours: "0h 0m",
      shortfallTime: "0h 0m",
    };

    if (!DatewiseData?.data || !Array.isArray(DatewiseData?.data) || DatewiseData?.data?.length === 0) {
      return defaults;
    }

    try {
      const inOutPairsList = [];
      const breakPairsList = [];
      let cumulativeWorkingMs = 0;
      let cumulativeBreakMs = 0;
      let companyBenefitFromBreakMs = 0;
      let companyBenefitFromWorkHoursMs = 0;
      let shortfallMs = 0;

      const LUNCH_BREAK_STANDARD_MS = 30 * 60 * 1000;
      const COMPANY_STANDARD_WORK_HOURS_MS = (8 * 60 + 30) * 60 * 1000;

      const allPunches = [];
      DatewiseData.data.forEach((item) => {
        if (!item) return;

        if (item.InDate) {
          try {
            const d = new Date(item.InDate);
            if (!isNaN(d.getTime())) {
              allPunches.push({
                type: "in",
                time: d,
                original: item.InDate,
              });
            }
          } catch (e) {
            console.error("Invalid InDate:", e);
          }
        }

        if (item.OutDate) {
          try {
            const d = new Date(item.OutDate);
            if (!isNaN(d.getTime())) {
              allPunches.push({
                type: "out",
                time: d,
                original: item.OutDate,
              });
            }
          } catch (e) {
            console.error("Invalid OutDate:", e);
          }
        }
      });

      if (allPunches.length === 0) return defaults;

      const sortedPunches = [...allPunches].sort(
        (a, b) => a.time.getTime() - b.time.getTime()
      );

      let lastInTime = null;
      let lastOutTimeForBreak = null;

      sortedPunches.forEach((punch) => {
        if (punch.type === "in") {
          if (lastOutTimeForBreak && !isNaN(lastOutTimeForBreak.getTime())) {
            const breakDurationMs = punch.time.getTime() - lastOutTimeForBreak.getTime();
            if (breakDurationMs > 0) {
              cumulativeBreakMs += breakDurationMs;
              const formattedOut = formatUTCToHHMM(lastOutTimeForBreak.toISOString()) || "--:--";
              const formattedIn = formatUTCToHHMM(punch.original) || "--:--";
              const durationStr = getDuration(
                lastOutTimeForBreak.toISOString(),
                punch.original
              ) || "0h 0m";

              breakPairsList.push({
                type: "break",
                display: `${formattedOut} → ${formattedIn} (Break: ${durationStr})`,
              });
            }
          }
          lastInTime = punch.time;
          lastOutTimeForBreak = null;
        } else if (punch.type === "out") {
          if (lastInTime && !isNaN(lastInTime.getTime())) {
            const workingDurationMs = punch.time.getTime() - lastInTime.getTime();
            if (workingDurationMs > 0) {
              cumulativeWorkingMs += workingDurationMs;
              const formattedIn = formatUTCToHHMM(lastInTime.toISOString()) || "--:--";
              const formattedOut = formatUTCToHHMM(punch.original) || "--:--";
              const durationStr = getDuration(
                lastInTime.toISOString(),
                punch.original
              ) || "0h 0m";

              inOutPairsList.push({
                type: "working",
                display: `${formattedIn} → ${formattedOut} (${durationStr})`,
              });
            }
            lastInTime = null;
          }
          lastOutTimeForBreak = punch.time;
        }
      });

      const totalBreakTimeFormatted = formatMillisecondsToHHMM(cumulativeBreakMs);
      let adjustedCumulativeBreakMs = 0;

      if (cumulativeBreakMs > LUNCH_BREAK_STANDARD_MS) {
        adjustedCumulativeBreakMs = cumulativeBreakMs - LUNCH_BREAK_STANDARD_MS;
        companyBenefitFromBreakMs = 0;
      } else {
        companyBenefitFromBreakMs = LUNCH_BREAK_STANDARD_MS - cumulativeBreakMs;
        adjustedCumulativeBreakMs = 0;
      }

      if (cumulativeWorkingMs < COMPANY_STANDARD_WORK_HOURS_MS) {
        shortfallMs = COMPANY_STANDARD_WORK_HOURS_MS - cumulativeWorkingMs;
        companyBenefitFromWorkHoursMs = 0;
      } else {
        shortfallMs = 0;
        companyBenefitFromWorkHoursMs = cumulativeWorkingMs - COMPANY_STANDARD_WORK_HOURS_MS;
      }

      return {
        inOutPairs: inOutPairsList,
        breakPairs: breakPairsList,
        totalWorkingTime: formatMillisecondsToHHMM(cumulativeWorkingMs),
        totalBreakTime: totalBreakTimeFormatted,
        adjustedTotalBreakTime: formatMillisecondsToHHMM(adjustedCumulativeBreakMs),
        companyBenefitTime: formatMillisecondsToHHMM(companyBenefitFromBreakMs),
        companyBenefitFromWorkHours: formatMillisecondsToHHMM(companyBenefitFromWorkHoursMs),
        shortfallTime: formatMillisecondsToHHMM(shortfallMs),
      };
    } catch (error) {
      console.error("Error calculating data:", error);
      return defaults;
    }
  }, [DatewiseData?.data]);

  const {
    inOutPairs,
    breakPairs,
    totalWorkingTime,
    totalBreakTime,
    adjustedTotalBreakTime,
    companyBenefitTime,
    companyBenefitFromWorkHours,
    shortfallTime,
  } = calculatedData;

  function convertRawToEvents(data, leavesList, selectedName) {
    if (!Array.isArray(data)) data = [];
    if (!Array.isArray(leavesList)) leavesList = [];
    const targetName = selectedName ? selectedName.trim().toLowerCase() : "";

    const events = [];
    const groupedByDate = {};

    try {
      data.forEach((item) => {
        if (!item) return;

        const inDateStr = item.InDate ? (typeof item.InDate === 'string' ? item.InDate : new Date(item.InDate).toISOString()) : null;
        const outDateStr = item.OutDate ? (typeof item.OutDate === 'string' ? item.OutDate : new Date(item.OutDate).toISOString()) : null;

        const dateKey = inDateStr
          ? inDateStr.slice(0, 10)
          : outDateStr
            ? outDateStr.slice(0, 10)
            : null;

        if (!dateKey) return;

        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = { firstIn: null, lastOut: null };
        }

        if (inDateStr) {
          try {
            const inTime = new Date(inDateStr.slice(0, 19));
            if (!isNaN(inTime.getTime())) {
              const currentFirstInTime = groupedByDate[dateKey].firstIn ? new Date(groupedByDate[dateKey].firstIn.slice(0, 19)) : null;
              if (!currentFirstInTime || inTime < currentFirstInTime) {
                groupedByDate[dateKey].firstIn = inDateStr;
              }
            }
          } catch (e) {
            console.error("Invalid InDate in event:", e);
          }
        }

        if (outDateStr) {
          try {
            const outTime = new Date(outDateStr.slice(0, 19));
            if (!isNaN(outTime.getTime())) {
              const currentLastOutTime = groupedByDate[dateKey].lastOut ? new Date(groupedByDate[dateKey].lastOut.slice(0, 19)) : null;
              if (!currentLastOutTime || outTime > currentLastOutTime) {
                groupedByDate[dateKey].lastOut = outDateStr;
              }
            }
          } catch (e) {
            console.error("Invalid OutDate in event:", e);
          }
        }
      });

      // Range generation: minDate to 31 days in future
      const today = new Date();
      let minDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      minDate.setHours(0, 0, 0, 0);

      const punchDates = Object.keys(groupedByDate).map(d => new Date(d));
      if (punchDates.length > 0) {
        const minPunch = new Date(Math.min(...punchDates.map(d => d.getTime())));
        minPunch.setHours(0, 0, 0, 0);
        if (minPunch < minDate) {
          minDate = minPunch;
        }
      }

      const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 31);
      endDate.setHours(0, 0, 0, 0);

      const isLateIn = (isoString, checkInStr) => {
        if (!isoString) return false;
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return false;
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const totalMinutes = hours * 60 + minutes;

        const [h, m] = (checkInStr || "10:00").split(":").map(Number);
        const target = (isNaN(h) ? 10 : h) * 60 + (isNaN(m) ? 0 : m);
        return totalMinutes > target;
      };

      const isEarlyOut = (isoString, checkOutStr) => {
        if (!isoString) return false;
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return false;
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const totalMinutes = hours * 60 + minutes;

        const [h, m] = (checkOutStr || "19:00").split(":").map(Number);
        const target = (isNaN(h) ? 19 : h) * 60 + (isNaN(m) ? 0 : m);
        return totalMinutes < target;
      };

      const formatYYYYMMDD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const todayStr = formatYYYYMMDD(today);
      const startTimestamp = minDate.getTime();
      const endTimestamp = endDate.getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;

      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      for (let time = startTimestamp; time <= endTimestamp; time += oneDayMs) {
        const d = new Date(time);
        const dayStr = formatYYYYMMDD(d);
        const dayOfWeek = d.getDay();
        const checkInTime = selectedMemberOption?.CheckInTime || LoginUserData?.data?.CheckInTime || "10:00";
        const checkOutTime = selectedMemberOption?.CheckOutTime || LoginUserData?.data?.CheckOutTime || "19:00";

        const [hIn, mIn] = checkInTime.split(":").map(Number);
        const targetInMinutes = (isNaN(hIn) ? 10 : hIn) * 60 + (isNaN(mIn) ? 0 : mIn);
        const isAbsentToday = (dayStr === todayStr && currentMinutes >= lastSchedulerInMinutes && currentMinutes >= targetInMinutes);
        const isPendingToday = (dayStr === todayStr && !isAbsentToday);

        const [hOut, mOut] = checkOutTime.split(":").map(Number);
        const targetOutMinutes = (isNaN(hOut) ? 19 : hOut) * 60 + (isNaN(mOut) ? 0 : mOut);
        const isPastCheckOutTimeToday = (dayStr === todayStr && currentMinutes >= targetOutMinutes);
        const canBeEarlyOut = dayStr < todayStr || isPastCheckOutTimeToday;

        const punch = groupedByDate[dayStr];
        if (punch && (punch.firstIn || punch.lastOut)) {
          const start = punch.firstIn ? new Date(punch.firstIn.slice(0, 19)) : null;
          const end = punch.lastOut ? new Date(punch.lastOut.slice(0, 19)) : start;
          const formattedIn = formatUTCToHHMM(punch.firstIn) || "--:--";
          const formattedOut = formatUTCToHHMM(punch.lastOut) || "--:--";

          const lateIn = isLateIn(punch.firstIn, checkInTime);
          const earlyOut = canBeEarlyOut && punch.lastOut ? isEarlyOut(punch.lastOut, checkOutTime) : false;
          const isRunning = (dayStr === todayStr) && !isPastCheckOutTimeToday;

          let status = 'present';
          let note = '';

          if (isRunning) {
            if (lateIn) {
              status = 'late';
              note = '(Late In - Present)';
            } else {
              status = 'running';
              note = '(Present)';
            }
          } else if (lateIn && earlyOut) {
            status = 'late-early';
            note = '(Late In & Early Out)';
          } else if (lateIn) {
            status = 'late';
            note = '(Late In)';
          } else if (earlyOut) {
            status = 'early-out';
            note = '(Early Out)';
          }

          const displayTitle = note ? `${formattedIn} - ${formattedOut} ${note}` : `${formattedIn} - ${formattedOut}`;

          events.push({
            title: displayTitle,
            start: start || d,
            end: end || start || d,
            tooltip: `In: ${formattedIn} | Out: ${formattedOut} ${note}`,
            status: status
          });
        } else {
          // Check for approved leaves
          const approvedLeave = leavesList.find(lv => {
            const isApproved = lv.Status === "Approved" || lv.status === "Approved";
            if (!isApproved) return false;
            const appName = (lv.applicantName || lv.applicant?.Name || "").trim().toLowerCase();
            return appName === targetName && lv.fromDate <= dayStr && lv.toDate >= dayStr;
          });

          if (approvedLeave) {
            const eventStart = new Date(d);
            eventStart.setHours(9, 0, 0, 0);
            const eventEnd = new Date(d);
            eventEnd.setHours(18, 0, 0, 0);

            events.push({
              title: `Leave: ${approvedLeave.LeaveType || 'Approved'}`,
              start: eventStart,
              end: eventEnd,
              tooltip: `Leave: ${approvedLeave.leaveReason || approvedLeave.reason || 'Approved Leave'}`,
              status: 'leave'
            });
          } else if (holidayMap.has(dayStr)) {
            const eventStart = new Date(d);
            eventStart.setHours(9, 0, 0, 0);
            const eventEnd = new Date(d);
            eventEnd.setHours(18, 0, 0, 0);

            const hName = holidayMap.get(dayStr);
            events.push({
              title: hName ? `Holiday: ${hName}` : 'Holiday',
              start: eventStart,
              end: eventEnd,
              tooltip: hName ? `Company Holiday: ${hName}` : 'Company Holiday',
              status: 'holiday'
            });
          } else if (dayOfWeek === 0) {
            const eventStart = new Date(d);
            eventStart.setHours(9, 0, 0, 0);
            const eventEnd = new Date(d);
            eventEnd.setHours(18, 0, 0, 0);

            events.push({
              title: 'Weekend Off',
              start: eventStart,
              end: eventEnd,
              tooltip: 'Sunday Weekend Off',
              status: 'weekend-off'
            });
          } else {
            // Check for absent weekdays in the past or after last in-time scheduler has fired today
            if (dayStr < todayStr || isAbsentToday) {
              const eventStart = new Date(d);
              eventStart.setHours(9, 0, 0, 0);
              const eventEnd = new Date(d);
              eventEnd.setHours(18, 0, 0, 0);

              events.push({
                title: 'Absent',
                start: eventStart,
                end: eventEnd,
                tooltip: 'No punch and no approved leave request found',
                status: 'absent'
              });
            } else if (isPendingToday) {
              const eventStart = new Date(d);
              eventStart.setHours(9, 0, 0, 0);
              const eventEnd = new Date(d);
              eventEnd.setHours(18, 0, 0, 0);

              events.push({
                title: 'Pending In-Time',
                start: eventStart,
                end: eventEnd,
                tooltip: 'Attendance pending - Waiting for Daily In-Time Scheduler execution',
                status: 'pending'
              });
            }
          }
        }
      }
    } catch (error) {
      console.error("Error converting to events:", error);
    }
    return events;
  }

  const calculateDurationInMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      const diffMs = end - start;
      return Math.floor(diffMs / (60 * 1000));
    } catch (error) {
      console.error("Error calculating duration:", error);
      return 0;
    }
  };

  const zigzagTableData = useMemo(() => {
    if (!Array.isArray(DatewiseData?.data) || DatewiseData.data.length === 0) return [];

    try {
      const punches = [];

      DatewiseData.data.forEach((item) => {
        if (!item) return;

        if (item.InDate) {
          try {
            const d = new Date(item.InDate);
            if (!isNaN(d.getTime())) {
              punches.push({
                type: "in",
                time: d,
                original: item.InDate,
              });
            }
          } catch (e) {
            console.error("Invalid InDate for zigzag:", e);
          }
        }

        if (item.OutDate) {
          try {
            const d = new Date(item.OutDate);
            if (!isNaN(d.getTime())) {
              punches.push({
                type: "out",
                time: d,
                original: item.OutDate,
              });
            }
          } catch (e) {
            console.error("Invalid OutDate for zigzag:", e);
          }
        }
      });

      if (punches.length === 0) return [];

      punches.sort((a, b) => a.time - b.time);

      const temp = [];
      let currentSessionStart = null;
      let lastPunchOut = null;

      punches.forEach((punch) => {
        if (punch.type === "in") {
          if (lastPunchOut && !isNaN(lastPunchOut.getTime())) {
            const duration = calculateDurationInMinutes(
              lastPunchOut.toISOString(),
              punch.original
            );

            const formattedOut = formatUTCToHHMM(lastPunchOut.toISOString()) || "--:--";
            const formattedIn = formatUTCToHHMM(punch.original) || "--:--";

            temp.push({
              type: "Break",
              description: `${formattedOut} - ${duration} min - ${formattedIn}`,
            });
          }
          currentSessionStart = punch.time;
        } else if (punch.type === "out") {
          if (currentSessionStart && !isNaN(currentSessionStart.getTime())) {
            const duration = calculateDurationInMinutes(
              currentSessionStart.toISOString(),
              punch.original
            );

            const formattedStart = formatUTCToHHMM(currentSessionStart.toISOString()) || "--:--";
            const formattedOut = formatUTCToHHMM(punch.original) || "--:--";

            temp.push({
              type: "Working Session",
              description: `${formattedStart} - ${duration} min - ${formattedOut}`,
            });
            currentSessionStart = null;
          }
          lastPunchOut = punch.time;
        }
      });

      return temp.map((entry, i) => ({
        id: i + 1,
        left: i % 2 === 0 ? entry.description : "-",
        right: i % 2 === 1 ? entry.description : "-",
      }));
    } catch (error) {
      console.error("Error generating zigzag table:", error);
      return [];
    }
  }, [DatewiseData?.data]);

  const zigzagTableColumns = [
    { field: "id", headerName: "ID", width: 60, align: "center" },
    { field: "left", headerName: "Left Time Slot", width: "calc(50% - 30px)", align: "center" },
    { field: "right", headerName: "Right Time Slot", width: "calc(50% - 30px)", align: "center" },
  ];

  const calendarEvents = useMemo(() => {
    const selectedMemberOption = memberOptions.find(opt => String(opt.value) === String(matchedValue));
    const selectedName = selectedMemberOption ? selectedMemberOption.label : LoginUserData?.data?.Name;
    return convertRawToEvents(MemberData?.data, leaveData, selectedName);
  }, [MemberData?.data, leaveData, matchedValue, memberOptions, LoginUserData, selectedMemberOption, holidayMap]);

  const [calendarContainerWidth, setCalendarContainerWidth] = useState(0);
  const calendarRef = useRef(null);
  const calendarScrollRef = useRef(null);

  const scrollCalendar = (direction) => {
    if (calendarScrollRef.current) {
      calendarScrollRef.current.scrollBy({
        left: direction === "left" ? -350 : 350,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const updateWidth = () => {
      if (calendarRef.current) {
        setCalendarContainerWidth(calendarRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const shouldShowDataSection = showData && !isLoading && DatewiseData?.data && DatewiseData.data.length > 0;

  // Show error if any
  if (calendarError) {
    return (
      <div ref={calendarRef} className="w-full overflow-x-auto">
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 mb-4 inline-block rounded">
            Payroll
          </h3>
        </div>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-4">
          <p>Error: {calendarError}</p>
          <button
            onClick={() => setCalendarError(null)}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (
    !payrollDropdownData || payrollDropdownData === null ||
    !MemberData || MemberData === null
  ) {
    return <Loader />
  }

  return (
    <>
      <div ref={calendarRef} className="w-full max-w-full overflow-x-hidden">
        <div className="title mb-4">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded shadow-sm">
            Payroll
          </h3>
        </div>

        <div className="calendar-section w-full max-w-full">
          {Array.isArray(MemberData?.data) && MemberData.data.length > 0 ? (
            <div className="bg-white p-3 sm:p-5 my-4 rounded-xl border border-gray-200 shadow-sm w-full max-w-full">
              {/* Color Legend Bar - Responsive wrapping on all screen sizes */}
              <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium w-full">
                <span className="font-bold text-slate-700 mr-1 text-xs">Status Legend:</span>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-blue-50 text-blue-800 border border-blue-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#1976d2] inline-block"></span>
                  <span>On-Time Present</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-teal-50 text-teal-800 border border-teal-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00897b] inline-block"></span>
                  <span>Present (Today)</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-amber-50 text-amber-900 border border-amber-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ffc107] inline-block"></span>
                  <span>Late In</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-orange-50 text-orange-900 border border-orange-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff9800] inline-block"></span>
                  <span>Early Out</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-orange-100 text-orange-950 border border-orange-400">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#e65100] inline-block"></span>
                  <span>Late In & Early Out</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-50 text-red-800 border border-red-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#d32f2f] inline-block"></span>
                  <span>Absent</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-100 text-slate-700 border border-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#64748b] inline-block"></span>
                  <span>Pending In-Time</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-sky-50 text-sky-800 border border-sky-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#0284c7] inline-block"></span>
                  <span>Weekend Off</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-purple-50 text-purple-800 border border-purple-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#9c27b0] inline-block"></span>
                  <span>Approved Leave</span>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16a34a] inline-block"></span>
                  <span>Holiday</span>
                </div>
              </div>

              {/* Informative Note for Employee - Full width with text wrap */}
              <div className="w-full mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs md:text-sm text-blue-900 flex items-start gap-2.5 shadow-xs">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="leading-relaxed">
                  <span className="font-bold">Please Note: </span>
                  Your daily attendance is calculated starting from <span className="font-semibold text-blue-950 underline">{formatTimeToShow(selectedMemberOption?.CheckInTime || LoginUserData?.data?.CheckInTime || "10:00")}</span> according to your working schedule (Your Working Hours: <span className="font-semibold text-blue-950">{formatTimeToShow(selectedMemberOption?.CheckInTime || LoginUserData?.data?.CheckInTime || "10:00")} - {formatTimeToShow(selectedMemberOption?.CheckOutTime || LoginUserData?.data?.CheckOutTime || "19:00")}</span>).
                </div>
              </div>

              {/* Calendar Header with Mobile Swipe Badge */}
              <div className="flex items-center justify-between py-1.5 px-1 mb-2 text-xs font-semibold text-slate-700">
                <span>Monthly Attendance Calendar</span>
                <span className="inline-flex lg:hidden items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200 text-[11px] font-semibold shadow-2xs">
                  ⇄ Swipe horizontally
                </span>
              </div>

              {/* DEDICATED CALENDAR HORIZONTAL SCROLLER */}
              <div
                ref={calendarScrollRef}
                className="calendar-scroll-wrapper w-full overflow-x-auto lg:overflow-x-visible rounded-t-xl lg:rounded-xl border border-slate-200 p-2 bg-white"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div className="min-w-[780px] lg:min-w-full">
                  <Calendar
                    key={matchedValue || 'calendar'}
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 500 }}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    tooltipAccessor={(event) => event.tooltip || ''}
                    eventPropGetter={(event) => {
                      const status = event.status || 'present';
                      let backgroundColor = "#1976d2";
                      let color = "white";

                      if (status === 'running') {
                        backgroundColor = "#00897b";
                        color = "white";
                      } else if (status === 'late') {
                        backgroundColor = "#ffc107";
                        color = "#000000";
                      } else if (status === 'early-out') {
                        backgroundColor = "#ff9800";
                        color = "#000000";
                      } else if (status === 'late-early') {
                        backgroundColor = "#e65100";
                        color = "#000000";
                      } else if (status === 'absent') {
                        backgroundColor = "#d32f2f";
                        color = "white";
                      } else if (status === 'pending') {
                        backgroundColor = "#64748b";
                        color = "white";
                      } else if (status === 'leave') {
                        backgroundColor = "#9c27b0";
                        color = "white";
                      } else if (status === 'holiday') {
                        backgroundColor = "#16a34a";
                        color = "white";
                      } else if (status === 'weekend-off') {
                        backgroundColor = "#0284c7";
                        color = "white";
                      }

                      return {
                        className: `rbc-event-${status}`,
                        style: {
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "0.75rem",
                          backgroundColor: `${backgroundColor} !important`,
                          color: `${color} !important`,
                          border: "none",
                          padding: "2px 4px",
                          borderRadius: "4px",
                        },
                      };
                    }}
                    dayPropGetter={dayPropGetter}
                  />
                </div>
              </div>

              {/* Mobile ONLY Clean Navigation Bar (Hidden on Desktop) */}
              <div className="flex lg:hidden items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-t-0 border-slate-200 rounded-b-xl mb-3">
                <button
                  type="button"
                  onClick={() => scrollCalendar("left")}
                  className="flex items-center gap-1 px-3 py-1 bg-white hover:bg-blue-50 text-blue-600 font-bold text-xs rounded-md border border-slate-300 shadow-2xs active:scale-95 transition-all cursor-pointer"
                >
                  <span>◀</span>
                  <span>Left</span>
                </button>

                <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
                  <span className="text-blue-600 font-bold">⇄</span>
                  <span>Swipe to View</span>
                </span>

                <button
                  type="button"
                  onClick={() => scrollCalendar("right")}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-md shadow-2xs active:scale-95 transition-all cursor-pointer"
                >
                  <span>Right</span>
                  <span>▶</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center mt-4 p-4 bg-gray-100 rounded">
              <h5>No calendar data available.</h5>
              {matchedValue ? (
                <p className="text-sm text-gray-600 mt-2">
                  No attendance records found for this member.
                </p>
              ) : (
                <p className="text-sm text-gray-600 mt-2">
                  Please wait while we load member data...
                </p>
              )}
            </div>
          )}
        </div>

        {isLoading && (
          <div className="text-center mt-10 p-4">
            <Loader />
          </div>
        )}

        {shouldShowDataSection && (
          <>
            <div className="flex flex-wrap gap-5 mt-5 justify-center">
              {/* In Time Block */}
              <div className="flex-1 min-w-[300px] bg-white shadow-md rounded-lg">
                <h3 className="text-green-600 mb-2 text-base sm:text-lg md:text-2xl text-center font-bold border border-gray-200 rounded-lg p-5 bg-white shadow-md">
                  In Time (Working Sessions)
                </h3>
                <div className="flex flex-col gap-2 mb-5 border border-gray-200 rounded-lg p-5 overflow-x-auto max-h-[400px]">
                  {inOutPairs.length > 0 ? (
                    inOutPairs.map((pair, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 rounded-md text-sm sm:text-base font-medium flex items-center justify-center bg-green-50 border border-green-300 text-green-700"
                      >
                        {pair.display}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500">No working sessions found for the selected date.</p>
                  )}
                </div>
                <div className="pt-4 border-t border-dashed border-gray-300 text-center">
                  <p className="mb-2 text-base text-gray-800 font-semibold">
                    Total Working Duration:
                  </p>
                  <p className="text-3xl font-medium mb-4 text-gray-800">
                    {totalWorkingTime}
                  </p>
                  {shortfallTime !== "0h 0m" && (
                    <div style={{ marginTop: "10px" }}>
                      <p className="text-sm sm:text-base md:text-xl mb-0 text-red-500 font-semibold">
                        Shortfall from Company Standard (8h 30m):
                      </p>
                      <h4 className="text-2xl font-bold text-red-500">
                        {shortfallTime}
                      </h4>
                    </div>
                  )}
                  {companyBenefitFromWorkHours !== "0h 0m" && (
                    <div className="mt-3">
                      <p className="mb-0 font-semibold text-green-600">
                        Company Benefit from Work Hours (Exceeded 8h 30m):
                      </p>
                      <h4 className="text-2xl font-bold text-green-600">
                        {companyBenefitFromWorkHours}
                      </h4>
                    </div>
                  )}
                </div>
              </div>

              {/* Out Time Block */}
              <div className="flex-1 min-w-[300px] bg-white shadow-md rounded-lg">
                <h3 className="text-yellow-600 mb-2 text-base sm:text-lg md:text-xl text-center font-bold border border-gray-200 rounded-lg p-5 bg-white shadow-md">
                  Out Time (Breaks Between Sessions)
                </h3>
                <div className="flex flex-col gap-2 mb-5 border border-gray-200 rounded-lg p-5 overflow-x-auto max-h-[400px]">
                  {breakPairs.length > 0 ? (
                    breakPairs.map((pair, idx) => (
                      <div
                        key={idx}
                        className="px-2 sm:px-3 py-2 rounded-md text-sm sm:text-base font-medium flex items-center justify-center bg-yellow-50 border border-yellow-300 text-yellow-700"
                      >
                        {pair.display}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500">
                      No breaks recorded between sessions for the selected date.
                    </p>
                  )}
                </div>
                <div className="pt-4 border-t border-dashed border-gray-300 text-center">
                  <p className="mb-2 text-lg text-gray-800 font-semibold">
                    Total Break Duration: {totalBreakTime}
                  </p>

                  {adjustedTotalBreakTime !== "0h 0m" && (
                    <>
                      <p className="text-muted fst-italic mb-0">
                        (30 minutes deducted for standard lunch)
                      </p>
                      <h4 className="text-lg lg:text-2xl font-bold text-red-500">
                        Adjusted Break Duration: {adjustedTotalBreakTime}
                      </h4>
                    </>
                  )}

                  {companyBenefitTime !== "0h 0m" && (
                    <>
                      <p className="text-muted font-medium italic mb-0">
                        (Time saved from standard lunch break)
                      </p>
                      <h4 className="text-2xl font-bold text-gray-700">
                        Company Benefit Time: {companyBenefitTime}
                      </h4>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Tables Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6 mt-5">
              <div className="">
                <h3 className="text-base sm:text-lg md:text-2xl font-semibold text-green-600 text-center mb-1 bg-white rounded-lg p-4 shadow">
                  Raw In/Out Punches
                </h3>
                <div className="rounded-lg p-1">
                  {inOutTableRows.length > 0 ? (
                    <PayrollTable columns={inOutTableColumns} rows={inOutTableRows} />
                  ) : (
                    <p className="p-4 bg-gray-50 rounded text-center text-gray-500">No punch data available</p>
                  )}
                </div>
              </div>

              <div className="">
                <h3 className="text-base sm:text-lg md:text-2xl font-semibold text-yellow-600 text-center mb-1 bg-white rounded-lg p-4 shadow">
                  Combined Sessions & Breaks
                </h3>
                <div className="rounded-lg p-1">
                  {zigzagTableData.length > 0 ? (
                    <PayrollTable
                      columns={zigzagTableColumns}
                      rows={zigzagTableData}
                    />
                  ) : (
                    <p className="p-4 bg-gray-50 rounded text-center text-gray-500">No session data available</p>
                  )}
                </div>
              </div>
            </div>
          </>

        )}

        {!shouldShowDataSection && !isLoading && matchedValue && (
          <div className="text-center my-4 p-4 bg-blue-50 rounded">
            <p>Click on a date in the calendar to view attendance details.</p>
          </div>
        )}

        <div className="">
          <InhouseMonthlyPayroll
            memberOptions={memberOptions}
            selectedMember={matchedValue}
          />
        </div>
      </div>
    </>
  );
};

export default InhousePayroll;